import { computed, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import LaisWhatsappConnectionsAPI from 'dashboard/api/laisWhatsappConnections';
import LaisWhatsappOperationsAPI from 'dashboard/api/laisWhatsappOperations';
import { catalogConnectionIsOpen } from '../utils/whatsappCatalog';

export function useWhatsappCatalog(selectedConnectionId) {
  const connections = ref([]);
  const meta = ref({});
  const products = ref([]);
  const catalog = ref(null);
  const isLoadingConnections = ref(false);
  const isLoadingProducts = ref(false);
  const isSaving = ref(false);
  const error = ref('');
  const lastUpdatedAt = ref(null);
  const nextCursor = ref('');
  const pageSize = ref(50);
  const currentPage = ref(1);
  const pageCursors = ref(['']);
  const pageCache = new Map();

  const selectedConnection = computed(() =>
    connections.value.find(
      connection => String(connection.id) === String(selectedConnectionId.value)
    )
  );
  const isConnected = computed(() =>
    catalogConnectionIsOpen(selectedConnection.value)
  );
  const canManage = computed(() => meta.value.admin === true);
  const hasNextPage = computed(() => Boolean(nextCursor.value));
  const hasPreviousPage = computed(() => currentPage.value > 1);
  const visibleProducts = computed(() =>
    products.value.filter(product => !product.is_hidden)
  );
  const hiddenProducts = computed(() =>
    products.value.filter(product => product.is_hidden)
  );

  const operationData = response => response?.data?.data || {};

  const loadConnections = async () => {
    isLoadingConnections.value = true;
    error.value = '';
    try {
      const { data } = await LaisWhatsappConnectionsAPI.list();
      connections.value = data.data || [];
      meta.value = data.meta || {};

      const requestedConnection = connections.value.find(
        connection =>
          String(connection.id) === String(selectedConnectionId.value)
      );
      if (!requestedConnection) {
        const activeConnection = connections.value.find(
          catalogConnectionIsOpen
        );
        selectedConnectionId.value =
          activeConnection?.id || connections.value[0]?.id || '';
      }
    } catch (requestError) {
      error.value =
        requestError.response?.data?.error ||
        'Não foi possível carregar as conexões de WhatsApp.';
    } finally {
      isLoadingConnections.value = false;
    }
  };

  const requestProducts = async after => {
    const response = await LaisWhatsappOperationsAPI.execute(
      selectedConnectionId.value,
      'list_catalog_products',
      { limit: pageSize.value, ...(after ? { after } : {}) }
    );
    return operationData(response);
  };

  const resetPagination = () => {
    currentPage.value = 1;
    nextCursor.value = '';
    pageCursors.value = [''];
    pageCache.clear();
  };

  const applyPage = (data, page) => {
    catalog.value = data;
    products.value = data.products || [];
    nextCursor.value = data.paging?.cursors?.after || '';
    pageCursors.value[page] = nextCursor.value;
    pageCache.set(page, data);
    currentPage.value = page;
    lastUpdatedAt.value = new Date();
  };

  const loadPage = async (page, { silent = false, force = false } = {}) => {
    if (!selectedConnectionId.value || isLoadingProducts.value) return;
    const cursor = pageCursors.value[page - 1];
    if (page > 1 && !cursor) return;

    isLoadingProducts.value = true;
    if (!silent) error.value = '';
    try {
      const data =
        !force && pageCache.has(page)
          ? pageCache.get(page)
          : await requestProducts(cursor);
      applyPage(data, page);
    } catch (requestError) {
      if (!silent) {
        error.value =
          requestError.response?.data?.error ||
          'Não foi possível carregar os produtos do catálogo.';
      }
    } finally {
      isLoadingProducts.value = false;
    }
  };

  const refreshProducts = async ({ silent = false, reset = false } = {}) => {
    if (reset) resetPagination();
    await loadPage(currentPage.value, { silent, force: true });
  };

  const loadNextPage = async () => {
    if (!hasNextPage.value || isLoadingProducts.value) return;

    await loadPage(currentPage.value + 1, { force: true });
  };

  const loadPreviousPage = async () => {
    if (!hasPreviousPage.value || isLoadingProducts.value) return;

    await loadPage(currentPage.value - 1);
  };

  const setPageSize = async value => {
    const nextPageSize = Number(value);
    if (![20, 50, 100].includes(nextPageSize)) return;

    pageSize.value = nextPageSize;
    resetPagination();
    await loadPage(1);
  };

  const findProduct = async productId => {
    const localProduct = products.value.find(
      product => String(product.id) === String(productId)
    );
    if (localProduct) return localProduct;

    const searchPage = async cursor => {
      const data = await requestProducts(cursor);
      const product = (data.products || []).find(
        item => String(item.id) === String(productId)
      );
      if (product) return product;
      const nextPage = data.paging?.cursors?.after || '';
      return nextPage ? searchPage(nextPage) : null;
    };

    return searchPage('');
  };

  const createProduct = async payload => {
    isSaving.value = true;
    try {
      const response = await LaisWhatsappOperationsAPI.execute(
        selectedConnectionId.value,
        'create_catalog_product',
        payload
      );
      return operationData(response);
    } finally {
      isSaving.value = false;
    }
  };

  const updateProduct = async (productId, payload) => {
    isSaving.value = true;
    try {
      const response = await LaisWhatsappOperationsAPI.execute(
        selectedConnectionId.value,
        'update_catalog_product',
        { product_id: productId, ...payload }
      );
      return operationData(response);
    } finally {
      isSaving.value = false;
    }
  };

  const deleteProduct = async productId => {
    isSaving.value = true;
    try {
      const response = await LaisWhatsappOperationsAPI.execute(
        selectedConnectionId.value,
        'delete_catalog_product',
        { product_id: productId }
      );
      return operationData(response);
    } finally {
      isSaving.value = false;
    }
  };

  const notifyError = requestError => {
    useAlert(
      requestError.response?.data?.error ||
        requestError.message ||
        'Não foi possível concluir a operação do catálogo.'
    );
  };

  return {
    connections,
    meta,
    products,
    catalog,
    isLoadingConnections,
    isLoadingProducts,
    isSaving,
    error,
    lastUpdatedAt,
    selectedConnection,
    isConnected,
    canManage,
    pageSize,
    currentPage,
    hasNextPage,
    hasPreviousPage,
    visibleProducts,
    hiddenProducts,
    loadConnections,
    refreshProducts,
    loadNextPage,
    loadPreviousPage,
    setPageSize,
    findProduct,
    createProduct,
    updateProduct,
    deleteProduct,
    notifyError,
  };
}
